/**
 * Rozpočet tovaru v editore zákazky (KOLO 230) — regály CRC vedľa nábytku.
 *
 * User: *„jedna zákazka, dva rozpočty… CP spolu"*, *„obchodníka nezaujíma,
 * kde ide hrana, iba cena"*. Tovar nemá materiály, hrany, rozmery ani
 * výrobu: zostava (regál R1) a pod ňou položky z cenníka tovaru — kód CRC,
 * povrch a farba (pri CO), Ks, cena. Ceny počíta server (réžia aj práca
 * tovaru 0, zisk varianty 0) a cena aktívneho tovaru je v cene zákazky.
 *
 * Od KOLA 241 (návrh T1 „tabuľka ako nábytok") vyzerá ako tabuľka dielcov
 * nábytku — tmavý pás zostavy s písmenom na dlaždici, počtom a cenou
 * v kapsulách, pruhované riadky, modrý výber — a vpravo je stĺpec ako
 * bočný panel nábytku: **Náhľad regálu | Súhrn**. Cena tovaru a Dať do
 * ceny sú v lište odborov a ⋮ verzie, Regál CRC a Pridať ▾ v hornom
 * paneli editora (modrý pás nad tabuľkou odišiel).
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, LayoutGrid, PackagePlus, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteDielec, useUpdateDielec } from "@/api/hooks/useDielce";
import { useDeleteZostava } from "@/api/hooks/useZostavy";
import { useTovarFarby, type TovarFarba } from "@/api/hooks/useTovar";
import { extractErrorMessage } from "@/api/client";
import type { Dielec, Varianta, ZakazkaDetail, Zostava } from "@/api/schemas";
import { formatEur, sklonuj } from "@/lib/format";
import { VYBRANY_RIADOK } from "@/lib/vzhladRiadku";
import { popisPovrchu, volitFarbu } from "@/lib/tovar";
import { PridatTovarDialog } from "@/components/tovar/PridatTovarDialog";
import { RegalDialog } from "@/components/tovar/RegalDialog";
import { NahladRegalu, type ZostavaNahladu } from "@/components/tovar/NahladRegalu";
import { cn } from "@/lib/utils";

/** Množstvo riadku — vlastná položka (montáž) má desatinné v svojej MJ. */
function mnozstvo(d: Dielec): number {
  if (d.vlastna_nakup != null) return Number(d.vlastna_mnozstvo ?? 1);
  return d.pocet_ks;
}

function mj(d: Dielec): string {
  if (d.vlastna_nakup != null) return d.vlastna_mj ?? "ks";
  return d.material_direct?.mj ?? "ks";
}

/** Cena za MJ a spolu za riadok (1 kus zostavy) — `cena_dielca` je za 1 ks,
 * pri vlastnej položke už za celé množstvo. */
function ceny(d: Dielec): { zaMj: number | null; spolu: number | null } {
  if (d.cena_dielca == null) return { zaMj: null, spolu: null };
  const c = Number(d.cena_dielca);
  if (d.vlastna_nakup != null) {
    const n = mnozstvo(d);
    return { zaMj: n > 0 ? c / n : c, spolu: c };
  }
  return { zaMj: c, spolu: c * d.pocet_ks };
}

function FarbaBunka({
  dielec,
  farby,
  onZmena,
}: {
  dielec: Dielec;
  farby: TovarFarba[];
  onZmena: (farba: string) => void;
}): JSX.Element {
  const m = dielec.material_direct;
  if (!volitFarbu(m)) {
    return <span className="text-muted-foreground">{popisPovrchu(m, null) ?? "—"}</span>;
  }
  const hodnota = dielec.farba ?? "";
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs text-muted-foreground">CO</span>
      <Select value={hodnota || undefined} onValueChange={onZmena}>
        <SelectTrigger
          className={cn("h-7 w-[150px] text-xs", !hodnota && "border-amber-500 text-amber-700")}
          aria-label={`Farba ${m?.nazov ?? ""}`}
        >
          <SelectValue placeholder="doplň farbu" />
        </SelectTrigger>
        <SelectContent>
          {farby.map((f) => (
            <SelectItem key={f.kod} value={f.kod} className="text-xs">
              <span className="font-mono">{f.kod}</span> {f.nazov}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PocetBunka({
  dielec,
  onUloz,
}: {
  dielec: Dielec;
  onUloz: (hodnota: number) => void;
}): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const zobraz = text ?? String(mnozstvo(dielec)).replace(".", ",");
  function uloz(): void {
    if (text === null) return;
    const n = Number(text.replace(",", "."));
    setText(null);
    if (!Number.isFinite(n) || n <= 0 || n === mnozstvo(dielec)) return;
    onUloz(n);
  }
  return (
    <Input
      value={zobraz}
      inputMode="decimal"
      className="h-7 w-16 text-right text-xs"
      aria-label={`Počet ${dielec.nazov_strojovy ?? ""}`}
      onChange={(e) => setText(e.target.value)}
      onBlur={uloz}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setText(null);
      }}
    />
  );
}

/** Vlastná položka bez ceny (nákup 0 — z konfigurátora regálu: zarážky, LED,
 * diel mimo cenníka, KOLO 232) — obchodník doplní nákup priamo v riadku. */
const bezCeny = (d: Dielec): boolean => d.vlastna_nakup != null && Number(d.vlastna_nakup) === 0;

function NakupBunka({
  dielec,
  onUloz,
}: {
  dielec: Dielec;
  onUloz: (nakup: string) => void;
}): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  function uloz(): void {
    if (text === null) return;
    const n = Number(text.replace(",", ".").replace(/\s|€/g, ""));
    setText(null);
    if (!Number.isFinite(n) || n <= 0) return;
    onUloz(String(n));
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      <Input
        value={text ?? ""}
        placeholder="nákup €"
        inputMode="decimal"
        className="h-7 w-20 border-amber-500 text-right text-xs"
        aria-label={`Nákupná cena ${dielec.nazov_strojovy ?? ""}`}
        onChange={(e) => setText(e.target.value)}
        onBlur={uloz}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setText(null);
        }}
      />
      <span className="whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
        doplň cenu
      </span>
    </div>
  );
}

/** Bunky riadku tovaru — rovnaké stĺpce ako hlavička tabuľky. */
const STLPCE = 9;

function ZostavaTovaru({
  zakazkaId,
  zostava,
  farby,
  rozbalena,
  onToggle,
  onPridat,
  onVlastna,
  onRegal,
  vybranyId,
  onVyberRiadok,
  onVyberZostavu,
  zostavaVybrana,
}: {
  zakazkaId: string;
  zostava: Zostava;
  farby: TovarFarba[];
  rozbalena: boolean;
  onToggle: () => void;
  onPridat?: (z: Zostava) => void;
  onVlastna?: (zostavaId: string) => void;
  /** Upraviť regál — zostava je z konfigurátora (KOLO 232); bez neho
   *  (rozpočtár — tovar len na čítanie) zostava tlačidlo regálu nemá. */
  onRegal?: (uid: string) => void;
  /** Vybraný riadok (id dielca) — modrý ako v tabuľke nábytku. */
  vybranyId: string | null;
  onVyberRiadok: (d: Dielec) => void;
  onVyberZostavu: () => void;
  zostavaVybrana: boolean;
}): JSX.Element {
  const { toast } = useToast();
  const update = useUpdateDielec(zakazkaId);
  const zmaz = useDeleteDielec(zakazkaId);
  const zmazZostavu = useDeleteZostava(zakazkaId);
  // Server posiela len aktívne riadky (zmazané nie)
  const riadky = (zostava.dielce ?? []) as Dielec[];
  const bezCien = riadky.filter(bezCeny).length;

  function chyba(err: unknown): void {
    toast({ variant: "destructive", title: "Zmena sa neuložila", description: extractErrorMessage(err) });
  }

  return (
    <tbody
      aria-label={`Zostava ${zostava.kod} ${zostava.nazov}`}
      data-regal-uid={zostava.regal_kluc ?? undefined}
      className="border-t-[10px] border-t-background first:border-t-0"
    >
      <tr
        className={cn(
          "cursor-pointer border-l-4 border-l-transparent font-medium",
          zostavaVybrana
            ? "bg-vyber-zostava text-white"
            : "bg-riadok-zostava text-riadok-zostava-text hover:bg-riadok-zostava-hover",
        )}
        aria-selected={zostavaVybrana}
        onClick={(e) => {
          // tlačidlá v páse majú vlastnú akciu
          if (!(e.target as Element).closest("button, input, select")) onVyberZostavu();
        }}
      >
        <td className="py-2 pl-2 pr-1">
          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={onToggle}
              aria-label={rozbalena ? "Zbaliť" : "Rozbaliť"}
              title="Zbaliť / rozbaliť zostavu"
              className="rounded text-riadok-zostava-ikona hover:text-white"
            >
              {rozbalena ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <span
              className={cn(
                "inline-grid h-6 min-w-[24px] place-items-center rounded-md bg-white px-1 font-mono text-sm font-bold",
                zostavaVybrana ? "text-vyber-zostava" : "text-riadok-zostava",
              )}
            >
              {zostava.kod}
            </span>
          </span>
        </td>
        <td colSpan={3} className="px-2 py-2">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{zostava.nazov}</span>
            {zostava.popis ? (
              <span className="truncate text-xs font-normal opacity-75">{zostava.popis}</span>
            ) : null}
            {zostava.regal_kluc ? (
              <span
                className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-900"
                title="Zostava z konfigurátora regálu — Upraviť regál nahradí jej položky, ručne pridané ostanú"
              >
                z konfigurátora
              </span>
            ) : null}
          </span>
        </td>
        <td className="px-2 py-2 text-right tabular-nums">{zostava.pocet_ks}</td>
        <td colSpan={3} className="px-2 py-2">
          <span className="flex items-center justify-end gap-2">
            <span
              className={cn(
                "whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs",
                rozbalena
                  ? "bg-riadok-zostava-kapsula text-riadok-zostava-tlmeny"
                  : "bg-white font-semibold text-riadok-zostava",
              )}
            >
              {rozbalena ? "" : "▸ "}
              {riadky.length} {sklonuj(riadky.length, ["položka", "položky", "položiek"])}
              {bezCien ? ` · ${bezCien} bez ceny` : ""}
            </span>
            <span
              className="whitespace-nowrap rounded-full bg-riadok-zostava-kapsula px-2.5 py-0.5 font-display text-sm font-semibold text-riadok-zostava-cena"
              title={zostava.pocet_ks > 1 ? `Celkom za ${zostava.pocet_ks} ks` : "Cena zostavy"}
            >
              {formatEur(zostava.cena_celkom)}
            </span>
          </span>
        </td>
        <td className="px-1 py-1 text-right [&_button]:text-riadok-zostava-ikona">
          <span className="flex items-center justify-end">
            {zostava.regal_kluc && onRegal ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 hover:bg-white/10"
                aria-label="Upraviť regál"
                title="Upraviť regál v konfigurátore"
                onClick={() => onRegal(zostava.regal_kluc as string)}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            {onPridat ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 hover:bg-white/10"
                aria-label={`Pridať tovar do ${zostava.kod}`}
                title="Tovar z cenníka CRC"
                onClick={() => onPridat(zostava)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            {onVlastna ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 hover:bg-white/10"
                aria-label={`Vlastná položka do ${zostava.kod}`}
                title="Montáž, doprava, položka mimo cenníka"
                onClick={() => onVlastna(zostava.id)}
              >
                <PackagePlus className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            {onPridat ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 hover:bg-white/10"
                aria-label={`Zmazať zostavu ${zostava.kod}`}
                title="Zmazať zostavu (Ctrl+Z vráti)"
                onClick={() => zmazZostavu.mutate(zostava.id, { onError: chyba })}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </span>
        </td>
      </tr>
      {!rozbalena ? null : riadky.length === 0 ? (
        <tr>
          <td colSpan={STLPCE} className="px-3 py-3 text-sm text-muted-foreground">
            Prázdna zostava — pridaj tovar z cenníka (+).
          </td>
        </tr>
      ) : (
        riadky.map((d, i) => {
          const c = ceny(d);
          const m = d.material_direct;
          const vybrany = d.id === vybranyId;
          return (
            <tr
              key={d.id}
              className={cn(
                "cursor-pointer border-l-4 border-l-transparent bg-riadok-dielec hover:bg-riadok-dielec-hover",
                i % 2 === 1 && "bg-riadok-dielec-parny",
                vybrany && VYBRANY_RIADOK,
              )}
              data-regal-kluc={d.regal_kluc ?? undefined}
              aria-selected={vybrany}
              onClick={(e) => {
                if ((e.target as Element).closest("button, input, select, [role=combobox]")) return;
                onVyberRiadok(d);
              }}
            >
              <td className={cn("py-1.5 pl-9 pr-1 font-mono text-xs", vybrany ? "text-vyber-id" : "text-muted-foreground")}>
                {d.kod}
              </td>
              <td className="px-2 py-1.5 align-top">
                {m?.tovar_kod ? (
                  <>
                    <div className="font-mono text-xs">{m.tovar_kod}</div>
                    {d.karta_abry ? (
                      <div className="font-mono text-[10px] text-muted-foreground" title="Karta ABRY">
                        ABRA {d.karta_abry}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">vlastná</span>
                )}
              </td>
              <td className="px-2 py-1.5 align-top">{d.nazov_strojovy ?? m?.nazov ?? "—"}</td>
              <td className="px-2 py-1.5 align-top">
                <FarbaBunka
                  dielec={d}
                  farby={farby}
                  onZmena={(farba) => update.mutate({ id: d.id, data: { farba } }, { onError: chyba })}
                />
              </td>
              <td className="px-2 py-1.5 text-right align-top">
                <PocetBunka
                  dielec={d}
                  onUloz={(n) =>
                    update.mutate(
                      {
                        id: d.id,
                        data:
                          d.vlastna_nakup != null
                            ? { vlastna_mnozstvo: String(n) }
                            : { pocet_ks: Math.round(n) },
                      },
                      { onError: chyba },
                    )
                  }
                />
              </td>
              <td className="px-2 py-1.5 align-top text-xs text-muted-foreground">{mj(d)}</td>
              <td className="px-2 py-1.5 text-right align-top tabular-nums">
                {bezCeny(d) ? (
                  <NakupBunka
                    dielec={d}
                    onUloz={(nakup) =>
                      update.mutate({ id: d.id, data: { vlastna_nakup: nakup } }, { onError: chyba })
                    }
                  />
                ) : (
                  formatEur(c.zaMj)
                )}
              </td>
              <td className="px-2 py-1.5 text-right align-top font-medium tabular-nums">{formatEur(c.spolu)}</td>
              <td className="px-1 py-1 align-top">
                {onPridat ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label={`Zmazať ${d.nazov_strojovy ?? m?.nazov ?? "riadok"}`}
                    onClick={() => zmaz.mutate(d.id, { onError: chyba })}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </td>
            </tr>
          );
        })
      )}
    </tbody>
  );
}

/** Súhrn tovaru v pravom stĺpci — cena s DPH a bez, v cene zákazky, bez ceny. */
function SuhrnTovaru({
  varianta,
  vCene,
  bezCien,
}: {
  varianta: Varianta;
  vCene: boolean;
  bezCien: number;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 text-sm" data-testid="suhrn-tovaru">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold uppercase tracking-wide text-muted-foreground">Tovar s DPH</span>
        <b className="font-display text-xl tabular-nums text-primary">{formatEur(varianta.cena_s_dph)}</b>
      </div>
      <div className="flex items-baseline justify-between gap-2 text-muted-foreground">
        <span>Bez DPH</span>
        <span className="tabular-nums">{formatEur(varianta.cena_bez_dph)}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {vCene ? (
          <>
            <span className="text-amber-500">★</span> v cene zákazky
          </>
        ) : (
          "nie je v cene zákazky — Dať do ceny je v ⋮ pri rozpočte"
        )}
      </p>
      {bezCien ? (
        <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {bezCien} {sklonuj(bezCien, ["položka", "položky", "položiek"])} bez ceny — doplň nákup
          v riadku (doplň cenu)
        </p>
      ) : null}
    </div>
  );
}

export function TovarVarianta({
  zakazka,
  varianta,
  onNovaZostava,
  onVlastna,
  lenCitanie = false,
  regal,
  onRegal,
  pridatDo,
  onPridatDo,
  onVyberZostavu,
}: {
  zakazka: ZakazkaDetail;
  varianta: Varianta;
  /** Otvorí okno novej zostavy (to isté ako + Zostava v paneli nástrojov). */
  onNovaZostava: () => void;
  /** Okno vlastnej položky editora (montáž, doprava). */
  onVlastna: (zostavaId: string) => void;
  /**
   * Tovar upravuje obchodník (KOLO 231) — rozpočtár ho vidí len na
   * čítanie: farba, Ks, + Tovar, Vlastná položka a zmazanie sú vypnuté.
   */
  lenCitanie?: boolean;
  /** Konfigurátor regálu (KOLO 232): `null` = zatvorený, `""` = celý regál,
   *  inak uid zostavy. Riadi ho editor — Regál CRC je v jeho paneli. */
  regal: string | null;
  onRegal: (uid: string | null) => void;
  /** Okno Pridať tovar — riadi editor (Pridať ▾ v jeho paneli). */
  pridatDo: Zostava | null;
  onPridatDo: (z: Zostava | null) => void;
  /** Vybraná zostava — cieľ Pridať ▾ v paneli editora. */
  onVyberZostavu?: (zostavaId: string | null) => void;
}): JSX.Element {
  const farby = useTovarFarby().data ?? [];
  const zostavy = (varianta.zostavy ?? []) as Zostava[];
  const maRegal = Boolean(varianta.ma_regal);
  const vCene = zakazka.aktivna_tovar_varianta_id === varianta.id;
  const bezCien = zostavy.reduce(
    (n, z) => n + ((z.dielce ?? []) as Dielec[]).filter(bezCeny).length,
    0,
  );
  const [zbalene, setZbalene] = useState<Set<string>>(new Set());
  // Výber: riadok (id dielca) alebo zostava; náhľad regálu ho sleduje
  // `kluc` = diel v náhľade — aj diel Tandemu, ktorý v tovare riadok nemá
  const [vyber, setVyber] = useState<{
    zostavaId: string | null;
    dielecId: string | null;
    kluc: string | null;
    posun?: number;
  }>({ zostavaId: null, dielecId: null, kluc: null });
  // Náhľad regálu (KOLO 237): zostavy z konfigurátora
  const regalZostavy: ZostavaNahladu[] = maRegal
    ? zostavy
        .filter((z) => z.regal_kluc)
        .map((z) => ({ uid: z.regal_kluc as string, kod: z.kod, nazov: z.nazov, pocet: z.pocet_ks }))
    : [];
  const zostavaVyberu = zostavy.find((z) => z.id === vyber.zostavaId) ?? null;
  const uidNahladu =
    (zostavaVyberu?.regal_kluc && regalZostavy.some((z) => z.uid === zostavaVyberu.regal_kluc)
      ? zostavaVyberu.regal_kluc
      : null) ??
    regalZostavy[0]?.uid ??
    null;
  const zostavaNahladu = zostavy.find((z) => z.regal_kluc === uidNahladu) ?? null;
  const klucVyberu = zostavaNahladu && vyber.zostavaId === zostavaNahladu.id ? vyber.kluc : null;
  const [panel, setPanel] = useState<"nahlad" | "suhrn">(uidNahladu ? "nahlad" : "suhrn");
  const koren = useRef<HTMLDivElement>(null);

  function vyberZostavu(
    zostavaId: string | null,
    dielecId: string | null,
    kluc: string | null = null,
    posun?: number,
  ): void {
    setVyber({ zostavaId, dielecId, kluc, posun });
    onVyberZostavu?.(zostavaId);
  }

  // klik na diel v obrázku → riadok do pohľadu
  useEffect(() => {
    if (!vyber.posun || !vyber.dielecId) return;
    const riadok = koren.current?.querySelector(`tr[aria-selected="true"][data-regal-kluc]`);
    riadok?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [vyber]);

  return (
    <div
      ref={koren}
      className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start"
      data-testid="tovar-varianta"
    >
      <fieldset disabled={lenCitanie} className="min-w-0 overflow-x-auto rounded-md border">
        {zostavy.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            <p>Rozpočet tovaru je prázdny.</p>
            {lenCitanie ? null : (
              <>
                <p className="mt-1">
                  Pridaj zostavu (napr. Regál R1) a do nej tovar z cenníka CRC, alebo poskladaj
                  regál v konfigurátore (Regál CRC).
                </p>
                <Button size="sm" className="mt-3" onClick={onNovaZostava}>
                  <Plus className="h-4 w-4" />
                  Zostava
                </Button>
              </>
            )}
          </div>
        ) : (
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead className="bg-tab-hlavicka text-[11px] uppercase tracking-wide text-tab-hlavicka-text">
              <tr className="text-left">
                <th className="w-[72px] py-2 pl-3 font-semibold">ID</th>
                <th className="w-[120px] px-2 font-semibold">Kód</th>
                <th className="px-2 font-semibold">Názov</th>
                <th className="w-[190px] px-2 font-semibold">Povrch / farba</th>
                <th className="w-[72px] px-2 text-right font-semibold">Ks</th>
                <th className="w-[44px] px-2 font-semibold">MJ</th>
                <th className="w-[110px] px-2 text-right font-semibold">Cena / MJ</th>
                <th className="w-[96px] px-2 text-right font-semibold">Spolu</th>
                <th className="w-[124px]" />
              </tr>
            </thead>
            {zostavy.map((z) => (
              <Fragment key={z.id}>
                <ZostavaTovaru
                  zakazkaId={zakazka.id}
                  zostava={z}
                  farby={farby}
                  rozbalena={!zbalene.has(z.id)}
                  onToggle={() =>
                    setZbalene((prev) => {
                      const n = new Set(prev);
                      if (n.has(z.id)) n.delete(z.id);
                      else n.add(z.id);
                      return n;
                    })
                  }
                  onPridat={lenCitanie ? undefined : onPridatDo}
                  onVlastna={lenCitanie ? undefined : onVlastna}
                  onRegal={lenCitanie ? undefined : (uid) => onRegal(uid)}
                  vybranyId={vyber.dielecId}
                  onVyberRiadok={(d) => {
                    vyberZostavu(z.id, d.id, d.regal_kluc ?? null);
                    if (d.regal_kluc && z.regal_kluc) setPanel("nahlad");
                  }}
                  onVyberZostavu={() => vyberZostavu(z.id, null)}
                  zostavaVybrana={vyber.zostavaId === z.id && vyber.dielecId === null}
                />
              </Fragment>
            ))}
          </table>
        )}
      </fieldset>

      {/* Pravý stĺpec ako bočný panel nábytku — Náhľad regálu | Súhrn;
          mimo fieldsetu, rozpočtár prepína pohľady tiež */}
      <div className="order-first flex flex-col gap-2 rounded-md border bg-card p-2 lg:order-none">
        <div className="flex gap-1 rounded-md bg-muted p-1" role="tablist" aria-label="Pravý panel tovaru">
          {uidNahladu ? (
            <button
              type="button"
              role="tab"
              aria-selected={panel === "nahlad"}
              onClick={() => setPanel("nahlad")}
              className={cn(
                "flex-1 rounded px-2 py-1 text-xs font-medium",
                panel === "nahlad" ? "bg-background shadow-sm" : "text-muted-foreground",
              )}
            >
              Náhľad regálu
            </button>
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={panel === "suhrn" || !uidNahladu}
            onClick={() => setPanel("suhrn")}
            className={cn(
              "flex-1 rounded px-2 py-1 text-xs font-medium",
              panel === "suhrn" || !uidNahladu ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            Súhrn
          </button>
        </div>
        {uidNahladu && panel === "nahlad" ? (
          <NahladRegalu
            variantaId={varianta.id}
            zostavy={regalZostavy}
            uid={uidNahladu}
            kluc={klucVyberu}
            kluceRiadkov={((zostavaNahladu?.dielce ?? []) as Dielec[])
              .map((d) => d.regal_kluc)
              .filter((k): k is string => Boolean(k))}
            onZostava={(uid) => vyberZostavu(zostavy.find((z) => z.regal_kluc === uid)?.id ?? null, null)}
            onKluc={(kluc) => {
              const d = ((zostavaNahladu?.dielce ?? []) as Dielec[]).find((x) => x.regal_kluc === kluc);
              vyberZostavu(zostavaNahladu?.id ?? null, d?.id ?? null, kluc, Date.now());
            }}
          />
        ) : (
          <div className="p-2">
            <SuhrnTovaru varianta={varianta} vCene={vCene} bezCien={bezCien} />
          </div>
        )}
      </div>

      {regal !== null ? (
        <RegalDialog
          zakazkaId={zakazka.id}
          variantaId={varianta.id}
          uid={regal || null}
          lenCitanie={lenCitanie}
          onOpenChange={(o) => {
            if (!o) onRegal(null);
          }}
        />
      ) : null}
      <PridatTovarDialog
        zakazkaId={zakazka.id}
        zostava={pridatDo}
        farby={farby}
        onOpenChange={(o) => {
          if (!o) onPridatDo(null);
        }}
      />
    </div>
  );
}
